<template>
    <div class="bible-nerd-row row mb-4">
        <div class="col-12">
            <section class="bible-nerd-corner" aria-labelledby="bible-nerd-corner-title">
                <header class="bible-nerd-header">
                    <div class="bible-nerd-icon" aria-hidden="true">
                        <i class="material-icons">auto_stories</i>
                    </div>
                    <div class="bible-nerd-heading">
                        <p class="bible-nerd-eyebrow">A little room for curiosity</p>
                        <h2 id="bible-nerd-corner-title">Bible Nerd Corner</h2>
                        <p>Explore thoughtful resources for going deeper into Scripture.</p>
                    </div>
                    <span class="bible-nerd-badge">Bible study resources</span>
                </header>

                <div class="bible-study-controls">
                    <div class="bible-study-control">
                        <label for="bible-study-record">Choose a resource</label>
                        <select
                            id="bible-study-record"
                            v-model="selectedBibleStudyRecordKey"
                            class="form-select"
                            :disabled="bibleStudyLoading || !bibleStudyRecords.length"
                        >
                            <option value="">All resources</option>
                            <option
                                v-for="record in bibleStudyRecords"
                                :key="record._recordKey"
                                :value="record._recordKey"
                            >
                                {{ getBibleStudyRecordName(record) }}
                            </option>
                        </select>
                    </div>
                    <div class="bible-study-control">
                        <label for="bible-study-search">Search resources</label>
                        <div class="bible-study-search">
                            <i class="material-icons" aria-hidden="true">search</i>
                            <input
                                id="bible-study-search"
                                v-model.trim="bibleStudySearch"
                                type="search"
                                class="form-control"
                                placeholder="Search by title, source, or topic"
                                :disabled="bibleStudyLoading || !bibleStudyRecords.length"
                            />
                        </div>
                    </div>
                </div>

                <div v-if="bibleStudyLoading" class="bible-study-status" role="status" aria-live="polite">
                    <span class="bible-study-spinner" aria-hidden="true"></span>
                    <span>Gathering your study resources…</span>
                </div>
                <div v-else-if="bibleStudyError" class="bible-study-message bible-study-error" role="alert">
                    <i class="material-icons" aria-hidden="true">error_outline</i>
                    <div>
                        <p>{{ bibleStudyError }}</p>
                        <button type="button" class="bible-study-retry" @click="fetchBibleStudyRecords">
                            Try again
                        </button>
                    </div>
                </div>
                <div v-else-if="!bibleStudyRecords.length" class="bible-study-message" role="status">
                    <i class="material-icons" aria-hidden="true">menu_book</i>
                    <p>No Bible study resources are available right now. Please check back later.</p>
                </div>
                <div v-else-if="!filteredBibleStudyRecords.length" class="bible-study-message" role="status">
                    <i class="material-icons" aria-hidden="true">search_off</i>
                    <div>
                        <p>No resources match your search or selection.</p>
                        <button
                            type="button"
                            class="bible-study-retry"
                            @click="selectedBibleStudyRecordKey = ''; bibleStudySearch = ''"
                        >
                            Show all resources
                        </button>
                    </div>
                </div>
                <div v-else class="bible-study-list" role="region" aria-label="Bible study resources" tabindex="0">
                    <article
                        v-for="record in filteredBibleStudyRecords"
                        :key="record._recordKey"
                        class="bible-study-card"
                    >
                        <div class="bible-study-image">
                            <img
                                v-if="getBibleStudyImage(record)"
                                :src="getBibleStudyImage(record)"
                                :alt="getBibleStudyImageAlt(record)"
                                loading="lazy"
                            />
                            <i v-else class="material-icons" aria-hidden="true">menu_book</i>
                        </div>
                        <div class="bible-study-card-body">
                            <div class="bible-study-card-meta">
                                <span class="bible-study-type">Bible study</span>
                                <span class="bible-study-source">
                                    <strong>Source:</strong> {{ getBibleStudySource(record) }}
                                </span>
                            </div>
                            <h3>{{ getBibleStudyRecordName(record) }}</h3>
                            <p v-if="record.content" class="bible-study-content">{{ record.content }}</p>
                            <p v-else class="bible-study-content bible-study-content-empty">
                                Open this resource to explore the study.
                            </p>
                            <a
                                v-if="getBibleStudyUrl(record)"
                                class="bible-study-open-link"
                                :href="getBibleStudyUrl(record)"
                                target="_blank"
                                rel="noopener noreferrer"
                                :aria-label="`Open ${getBibleStudyRecordName(record)} (opens in a new tab)`"
                                @click="trackBibleStudyClick(record)"
                            >
                                <span>Explore resource</span>
                                <i class="material-icons" aria-hidden="true">open_in_new</i>
                            </a>
                            <span v-else class="bible-study-link-disabled" aria-disabled="true">
                                Resource link unavailable
                            </span>
                        </div>
                    </article>
                </div>
                <p
                    v-if="!bibleStudyLoading && !bibleStudyError && bibleStudyRecords.length && filteredBibleStudyRecords.length"
                    class="bible-study-count"
                    aria-live="polite"
                >
                    Showing {{ filteredBibleStudyRecords.length }} of {{ bibleStudyRecords.length }} resources
                </p>
            </section>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import api from '@/config/api';

const props = defineProps({
    locationId: {
        type: [String, Number],
        default: null,
    },
});

const bibleStudyRecords = ref([]);
const bibleStudyLoading = ref(false);
const bibleStudyError = ref('');
const bibleStudySearch = ref('');
const selectedBibleStudyRecordKey = ref('');
let bibleStudyRequestId = 0;

const filteredBibleStudyRecords = computed(() => {
    let records = bibleStudyRecords.value;
    if (selectedBibleStudyRecordKey.value) {
        records = records.filter(
            (record) => record._recordKey === selectedBibleStudyRecordKey.value
        );
    }

    const search = bibleStudySearch.value.trim().toLowerCase();
    if (!search) {
        return records;
    }

    return records.filter((record) => {
        const searchableText = [
            getBibleStudyRecordName(record),
            record.content,
            getBibleStudySource(record),
        ]
            .filter((value) => value != null)
            .join(' ')
            .toLowerCase();
        return searchableText.includes(search);
    });
});

async function fetchBibleStudyRecords() {
    const requestId = ++bibleStudyRequestId;
    bibleStudyLoading.value = true;
    bibleStudyError.value = '';
    bibleStudyRecords.value = [];
    selectedBibleStudyRecordKey.value = '';

    try {
        const response = await api.get('/ads', {
            params: { record_type: 'biblestudy' },
        });
        if (requestId !== bibleStudyRequestId) {
            return;
        }

        const output = response?.data?.output;
        const returncode = output?.returncode;
        if (returncode !== undefined && returncode !== 0 && returncode !== '0') {
            throw new Error(output?.message || 'Unable to load Bible study resources.');
        }
        if (!Array.isArray(output?.ads)) {
            throw new Error(output?.message || 'The Bible study resource list could not be loaded.');
        }

        bibleStudyRecords.value = output.ads
            .filter((record) => record?.active === true && record?.record_type === 'biblestudy')
            .map((record, index) => {
                const recordId = record.record_id
                    ?? record.id
                    ?? record.recordid
                    ?? record.recordId
                    ?? null;
                return {
                    ...record,
                    _recordId: recordId,
                    _recordKey: recordId == null ? `biblestudy-${index}` : `${recordId}-${index}`,
                };
            });
    } catch (error) {
        if (requestId !== bibleStudyRequestId) {
            return;
        }
        console.error('Error fetching Bible study resources:', error);
        bibleStudyError.value = error?.response?.data?.output?.message
            || error?.response?.data?.message
            || error?.message
            || 'Unable to load Bible study resources. Please try again.';
    } finally {
        if (requestId === bibleStudyRequestId) {
            bibleStudyLoading.value = false;
        }
    }
}

function getBibleStudyRecordName(record) {
    return typeof record?.name === 'string' && record.name.trim()
        ? record.name.trim()
        : 'Bible study resource';
}

function getBibleStudyImage(record) {
    const image = record?.image_src ?? record?.image_url ?? record?.image;
    return typeof image === 'string' ? image.trim() : '';
}

function getBibleStudyImageAlt(record) {
    return typeof record?.image_alt === 'string' && record.image_alt.trim()
        ? record.image_alt.trim()
        : getBibleStudyRecordName(record);
}

function getBibleStudySource(record) {
    return typeof record?.source === 'string' && record.source.trim()
        ? record.source.trim()
        : 'Bible study resource';
}

function getBibleStudyUrl(record) {
    const url = typeof record?.url === 'string' ? record.url.trim() : '';
    if (!url) {
        return '';
    }

    try {
        const parsedUrl = new URL(url, window.location.origin);
        return ['http:', 'https:'].includes(parsedUrl.protocol) ? parsedUrl.href : '';
    } catch {
        return '';
    }
}

function trackBibleStudyClick(record) {
    const recordId = record?._recordId;
    if (recordId == null || props.locationId == null || props.locationId === '') {
        return;
    }

    api.post('/ads/bible-study-click', {
        record_id: recordId,
        location_id: props.locationId,
    }).catch((error) => {
        console.warn('Unable to track Bible study click:', error);
    });
}

onMounted(fetchBibleStudyRecords);
</script>

<style scoped>
.bible-nerd-corner {
    overflow: hidden;
    border: 1px solid #e6eaf2;
    border-radius: 18px;
    background: #fff;
    box-shadow: 0 10px 30px rgba(52, 71, 103, 0.08);
}
.bible-nerd-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem 1.75rem;
    color: #fff;
    background:
        radial-gradient(circle at 88% 10%, rgba(255, 255, 255, 0.2), transparent 24%),
        linear-gradient(120deg, #344767, #5e72e4 65%, #7a89ef);
}
.bible-nerd-icon {
    display: flex;
    width: 54px;
    height: 54px;
    flex: 0 0 54px;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.14);
}
.bible-nerd-icon i {
    font-size: 29px;
}
.bible-nerd-heading {
    flex: 1;
    min-width: 0;
}
.bible-nerd-heading h2 {
    margin: 0;
    color: inherit;
    font-size: 1.35rem;
    font-weight: 700;
}
.bible-nerd-heading p {
    margin: 0.3rem 0 0;
    color: rgba(255, 255, 255, 0.82);
    font-size: 0.86rem;
}
.bible-nerd-heading .bible-nerd-eyebrow {
    margin: 0 0 0.25rem;
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}
.bible-nerd-badge {
    padding: 0.5rem 0.8rem;
    border: 1px solid rgba(255, 255, 255, 0.32);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 0.73rem;
    font-weight: 600;
    white-space: nowrap;
}
.bible-study-controls {
    display: grid;
    grid-template-columns: minmax(220px, 0.8fr) minmax(260px, 1.2fr);
    gap: 1rem;
    padding: 1.25rem 1.5rem 0;
}
.bible-study-control {
    min-width: 0;
}
.bible-study-control label {
    display: block;
    margin-bottom: 0.4rem;
    color: #344767;
    font-size: 0.78rem;
    font-weight: 700;
}
.bible-study-control .form-select,
.bible-study-search .form-control {
    min-height: 44px;
    border-color: #e2e6ef;
    border-radius: 10px;
    color: #344767;
    font-size: 0.86rem;
}
.bible-study-search {
    position: relative;
}
.bible-study-search > i {
    position: absolute;
    top: 50%;
    left: 0.8rem;
    z-index: 1;
    color: #8392ab;
    font-size: 19px;
    transform: translateY(-50%);
}
.bible-study-search .form-control {
    padding-left: 2.5rem;
}
.bible-study-list {
    display: grid;
    max-height: 540px;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
    gap: 1rem;
    overflow-y: auto;
    margin: 1rem 1.5rem 0;
    padding: 0.25rem 0.5rem 0.75rem 0.25rem;
    overscroll-behavior: contain;
}
.bible-study-list:focus-visible {
    outline: 3px solid rgba(94, 114, 228, 0.35);
    outline-offset: 2px;
}
.bible-study-card {
    display: flex;
    min-height: 176px;
    overflow: hidden;
    border: 1px solid #e8ebf2;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 3px 12px rgba(52, 71, 103, 0.05);
    transition: box-shadow 0.18s ease, transform 0.18s ease;
}
.bible-study-card:hover {
    box-shadow: 0 8px 22px rgba(52, 71, 103, 0.11);
    transform: translateY(-2px);
}
.bible-study-image {
    display: flex;
    width: 132px;
    flex: 0 0 132px;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: linear-gradient(145deg, #eef1ff, #f7f5ef);
    color: #5e72e4;
}
.bible-study-image img {
    width: 100%;
    height: 100%;
    min-height: 176px;
    object-fit: cover;
}
.bible-study-image > i {
    font-size: 38px;
}
.bible-study-card-body {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem 1.1rem;
}
.bible-study-card-meta {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.45rem;
    margin-bottom: 0.5rem;
}
.bible-study-type {
    padding: 0.24rem 0.55rem;
    border-radius: 999px;
    background: #eef1ff;
    color: #5264bd;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}
.bible-study-source {
    color: #7b809a;
    font-size: 0.7rem;
}
.bible-study-source strong {
    color: #5c6680;
    font-weight: 700;
}
.bible-study-card-body h3 {
    margin: 0;
    color: #344767;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.35;
}
.bible-study-content {
    display: -webkit-box;
    overflow: hidden;
    margin: 0.45rem 0 0.85rem;
    color: #68758e;
    font-size: 0.8rem;
    line-height: 1.5;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
}
.bible-study-content-empty {
    font-style: italic;
}
.bible-study-open-link {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    margin-top: auto;
    color: #5264bd;
    font-size: 0.78rem;
    font-weight: 700;
    text-decoration: none;
}
.bible-study-open-link:hover {
    color: #344767;
    text-decoration: underline;
}
.bible-study-open-link i {
    font-size: 16px;
}
.bible-study-link-disabled {
    margin-top: auto;
    color: #7b809a;
    font-size: 0.75rem;
}
.bible-study-status,
.bible-study-message {
    display: flex;
    min-height: 140px;
    align-items: center;
    justify-content: center;
    gap: 0.7rem;
    padding: 1.5rem;
    color: #68758e;
    text-align: center;
}
.bible-study-status {
    flex-direction: column;
}
.bible-study-status > span:last-child,
.bible-study-message p {
    margin: 0;
    font-size: 0.88rem;
}
.bible-study-message > i {
    color: #8392ab;
    font-size: 25px;
}
.bible-study-error > i {
    color: #d9534f;
}
.bible-study-error {
    color: #7c3d3a;
}
.bible-study-spinner {
    width: 30px;
    height: 30px;
    border: 3px solid #e4e8f2;
    border-top-color: #5e72e4;
    border-radius: 50%;
    animation: bible-study-rotation 0.8s linear infinite;
}
.bible-study-retry {
    margin-top: 0.4rem;
    padding: 0;
    border: 0;
    background: none;
    color: #5264bd;
    font-size: 0.8rem;
    font-weight: 700;
    text-decoration: underline;
}
.bible-study-count {
    margin: 0;
    padding: 0 1.75rem 1.1rem;
    color: #7b809a;
    font-size: 0.72rem;
    text-align: right;
}

@keyframes bible-study-rotation {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 767px) {
    .bible-nerd-header {
        align-items: flex-start;
        flex-wrap: wrap;
        padding: 1.2rem;
    }
    .bible-nerd-icon {
        width: 46px;
        height: 46px;
        flex-basis: 46px;
    }
    .bible-nerd-heading h2 {
        font-size: 1.15rem;
    }
    .bible-nerd-heading p:not(.bible-nerd-eyebrow) {
        font-size: 0.78rem;
    }
    .bible-nerd-badge {
        margin-left: 3.5rem;
    }
    .bible-study-controls {
        grid-template-columns: 1fr;
        padding: 1rem 1rem 0;
    }
    .bible-study-list {
        max-height: 620px;
        grid-template-columns: 1fr;
        margin: 0.75rem 1rem 0;
        padding-right: 0.25rem;
    }
    .bible-study-count {
        padding: 0 1rem 1rem;
    }
}

@media (max-width: 480px) {
    .bible-study-card {
        flex-direction: column;
    }
    .bible-study-image {
        width: 100%;
        height: 150px;
        flex-basis: 150px;
    }
    .bible-study-image img {
        min-height: 150px;
    }
}
</style>
